import type { Task } from '../../../api/tasks-client/task-client.type';

/** Option value prefix for a task picked from the recent-searches group. */
const RECENT_OPTION_PREFIX = 'recent:';

/** Option value prefix for a task returned by the live search. */
const RESULT_OPTION_PREFIX = 'task:';

/** Option value of the "Clear recent searches" action row. */
const CLEAR_RECENT_OPTION_VALUE = 'action:clear-recent';

/**
 * One row of the Task dropdown, in the order the rows are rendered. The combobox
 * reports a highlighted option only by index, so this list is what maps that
 * index back to the task (and to whether Backspace may remove it).
 */
type TaskSelectOption =
  | { kind: 'recent'; value: string; task: Task }
  | { kind: 'clear-recent'; value: string }
  | { kind: 'result'; value: string; task: Task };

type RecentTaskSelectOption = Extract<TaskSelectOption, { kind: 'recent' }>;
type ResultTaskSelectOption = Extract<TaskSelectOption, { kind: 'result' }>;

/**
 * Formats a task as it is shown in the Task dropdown and, once picked, in the field:
 * the external key taken from the last URL segment, then the name.
 *
 * @param {Task} task - The task to label; `url` may be empty
 * @returns {string} The display label, e.g. `BFK-1234 - Fix login`
 */
function getTaskOptionLabel(task: Task): string {
  return `${task.url ? task.url.split('/').pop() : ''} - ${task.name}`;
}

/**
 * Narrows the recent-search entries to those whose label contains the typed text,
 * case-insensitively. Empty or whitespace-only text keeps every entry, which is the
 * "show the list on focus" case.
 *
 * @param {Task[]} entries - Recent picks, most recent first
 * @param {string} text - What the user has typed so far
 * @returns {Task[]} The matching entries in their original order
 */
function filterTaskSearchHistory(entries: Task[], text: string): Task[] {
  const needle = text.trim().toLowerCase();
  if (!needle) {
    return entries;
  }
  return entries.filter((entry) => getTaskOptionLabel(entry).toLowerCase().includes(needle));
}

/**
 * Builds the ordered option list for the dropdown: the matching recent picks, a
 * "clear" action when any are shown, then the live search results. A result that
 * is already listed as a recent pick is left out, since both rows apply the same
 * task and a duplicate label is noise for sighted and screen-reader users alike.
 *
 * @param {Task[]} recentMatches - Recent picks that match the typed text
 * @param {Task[]} results - Tasks returned by the live search
 * @returns {TaskSelectOption[]} The rows in render order
 */
function buildTaskSelectOptions(recentMatches: Task[], results: Task[]): TaskSelectOption[] {
  const recentIds = new Set(recentMatches.map((task) => task.id));
  const recentOptions: TaskSelectOption[] = recentMatches.map((task) => ({
    kind: 'recent',
    value: `${RECENT_OPTION_PREFIX}${task.id}`,
    task,
  }));
  const clearOption: TaskSelectOption[] =
    recentMatches.length > 0 ? [{ kind: 'clear-recent', value: CLEAR_RECENT_OPTION_VALUE }] : [];
  const resultOptions: TaskSelectOption[] = results
    .filter((task) => !recentIds.has(task.id))
    .map((task) => ({
      kind: 'result',
      value: `${RESULT_OPTION_PREFIX}${task.id}`,
      task,
    }));

  return [...recentOptions, ...clearOption, ...resultOptions];
}

/**
 * Type guard for the recent-pick rows of the option list.
 *
 * @param {TaskSelectOption} option - Any dropdown row
 * @returns {boolean} True when the row is a recent pick
 */
function isRecentOption(option: TaskSelectOption): option is RecentTaskSelectOption {
  return option.kind === 'recent';
}

/**
 * Type guard for the live-result rows of the option list.
 *
 * @param {TaskSelectOption} option - Any dropdown row
 * @returns {boolean} True when the row is a live search result
 */
function isResultOption(option: TaskSelectOption): option is ResultTaskSelectOption {
  return option.kind === 'result';
}

export {
  buildTaskSelectOptions,
  CLEAR_RECENT_OPTION_VALUE,
  filterTaskSearchHistory,
  getTaskOptionLabel,
  isRecentOption,
  isResultOption,
};
export type { RecentTaskSelectOption, ResultTaskSelectOption, TaskSelectOption };
